import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useSettings } from "@/context/SettingsContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, Phone, MapPin, Star } from "lucide-react";

export default function Contact() {
  const { t, tl } = useI18n();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "", id_type: "cedula", id_number: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, kind: "contact" });
      toast.success(t("common.success"));
      setForm({ name: "", email: "", message: "", id_type: "cedula", id_number: "" });
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicLayout>
      <Seo
        title="Contacto & Ubicación — Frenchies Fitness & Pilates Coronado, Panamá"
        description="Visítanos en Coronado Padel Center, Nueva Gorgona, Coronado (Panamá). Escríbenos por WhatsApp o formulario para reservar tu clase de Fitness o Pilates."
        path="/contact"
        keywords="Frenchies Coronado ubicación, gym Coronado dirección, Sport Coronado, Fitness Coronado, contacto Pilates Coronado"
      />
      <section className="mx-auto grid max-w-6xl gap-12 px-5 py-12 md:grid-cols-2 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("nav.contact")}</h1>
          <div className="mt-8 space-y-4 text-sm">
            <p className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" />{settings?.contact?.email}</p>
            <p className="flex items-center gap-3"><Phone className="h-5 w-5 text-primary" />{settings?.contact?.phone}</p>
            <p className="flex items-center gap-3"><MapPin className="h-5 w-5 text-primary" />{settings?.contact?.address_fitness}</p>
            <p className="flex items-center gap-3"><MapPin className="h-5 w-5 text-primary" />{settings?.contact?.address_pilates}</p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <WhatsAppButton floating={false} />
            {settings?.google_review_url && (
              <a href={settings.google_review_url} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
                <Star className="h-4 w-4" /> {tl({ es: "Déjanos una reseña", en: "Leave us a review", fr: "Laisse-nous un avis" })}
              </a>
            )}
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-7">
          <div className="space-y-4">
            <Input data-testid="contact-name" placeholder={t("auth.firstName")} value={form.name} required
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" />
            <Input data-testid="contact-email" type="email" placeholder={t("auth.email")} value={form.email} required
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
            <div className="grid grid-cols-3 gap-3">
              <select data-testid="contact-id-type" value={form.id_type}
                onChange={(e) => setForm({ ...form, id_type: e.target.value })}
                className="col-span-1 h-12 rounded-lg border border-border bg-card px-3 text-sm">
                <option value="cedula">Cédula</option>
                <option value="ruc">RUC</option>
                <option value="pasaporte">{tl({ es: "Pasaporte", en: "Passport", fr: "Passeport" })}</option>
              </select>
              <Input data-testid="contact-id-number" placeholder={tl({ es: "N° de documento", en: "Document no.", fr: "N° de document" })}
                value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="col-span-2 h-12" />
            </div>
            <Textarea data-testid="contact-message" placeholder="..." value={form.message} required rows={5}
              onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <Button type="submit" data-testid="contact-submit" disabled={sending} className="w-full rounded-full">
              {sending ? t("common.loading") : t("common.confirm")}
            </Button>
          </div>
        </form>
      </section>
      <section className="mx-auto max-w-6xl px-5 pb-16 lg:px-8">
        <iframe title="Frenchies Fitness & Pilates — Coronado, Panamá" data-testid="contact-map"
          src={`https://www.google.com/maps?q=${encodeURIComponent((settings?.contact?.address_fitness || "Coronado") + ", Coronado, Panamá")}&output=embed`}
          className="h-[360px] w-full rounded-2xl border border-border" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </section>
    </PublicLayout>
  );
}
